import type { ApiClient } from 'src/utils/apiClient'
import type { MethodsBuilder, Service, ServiceMethods } from './types'

function baseMethods<T, TCreate = Partial<T>, TUpdate = Partial<T>>(
  client: ApiClient,
  url: string,
): ServiceMethods<T, TCreate, TUpdate> {
  return {
    get: (params?) => client.get<T>(url, params),
    getAll: (params?) => client.get<T[]>(url, params),
    getById: (id, params?) =>
      client.get<T>(`${url}/${encodeURIComponent(id)}`, params),
    create: (data, params?) => client.post<T>(url, data, params),
    update: (id, data, params?) =>
      client.put<T>(`${url}/${encodeURIComponent(id)}`, data, params),
    patch: (id, data, params?) =>
      client.patch<T>(`${url}/${encodeURIComponent(id)}`, data, params),
    remove: (id, params?) =>
      client.del<void>(`${url}/${encodeURIComponent(id)}`, { params }),
    removeBody: (id, params?) => client.del<void>(url, { data: [id], params }),
  }
}

export function createService(client: ApiClient, baseUrl: string): Service {
  return {
    endpoint<T, TCreate = Partial<T>, TUpdate = Partial<T>>() {
      return <TArgs extends unknown[], R extends Record<string, any>>(
        pathOrBuildUri: string | ((...args: TArgs) => string),
        builder: MethodsBuilder<T, R, TCreate, TUpdate>,
      ) => {
        if (typeof pathOrBuildUri === 'string') {
          const url = `${baseUrl}/${pathOrBuildUri}`
          return builder(
            baseMethods<T, TCreate, TUpdate>(client, url),
            client,
            url,
          )
        }
        return (...args: TArgs) => {
          const url = `${baseUrl}/${pathOrBuildUri(...args)}`
          return builder(
            baseMethods<T, TCreate, TUpdate>(client, url),
            client,
            url,
          )
        }
      }
    },
  }
}
